import React, { useState, useEffect, useRef } from "react";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

const card_ = (theme) => ({
  // width: 350,
  width: 446,
  marginBottom: "2px",
  border: "1px solid rgba(0, 0, 0, 0.12)",
  zIndex: 999,
  opacity: 0.975,
  "&:hover .MuiIconButton-root": {
    // show close btn
    visibility: "visible",
  },
  [theme.breakpoints.down("sm")]: {
    width: 330,
    marginBottom: "0px",
    opacity: 0.95,
    "& .MuiIconButton-root": {
      // always show close btn in sm
      visibility: "visible",
    },
  },
});

const closeBtn_ = (theme) => ({
  visibility: "hidden",
});

const expand_ = (theme) => ({
  transform: "rotate(0deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
});

const expandOpen_ = (theme) => ({
  transform: "rotate(180deg)",
});

const cardAction_ = (theme) => ({
  padding: "0px",
  "&:hover": {
    cursor: "pointer",
  },
  [theme.breakpoints.down("sm")]: {
    "& > .MuiIconButton-root": {
      padding: "8px",
    },
  },
  transition: theme.transitions.create("backgroundColor", {
    duration: theme.transitions.duration.short,
    delay: 0,
  }),
});

const cardActionFlash_ = (theme) => ({
  backgroundColor: "rgba(121, 134, 203, 0.5)",
});

const subtitle_ = (theme) => ({
  marginLeft: "auto",
  letterSpacing: "0.01em",
  maxWidth: 170,
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
});

const cardContent_ = (theme) => ({
  padding: "0px 8px",
  // maxHeight: 300,
  maxHeight: 600,
  overflow: "auto",
  "&:last-child": {
    paddingBottom: "8px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "2px !important",
  },
});

export default function Accordion(props) {
  const {
    children,
    title,
    subtitle,
    visible,
    expand,
    visibleHandler,
    expandHandler,
    getContainerRef,
    action = false,
  } = props;

  const containerRef = useRef(null);

  const [isVisible, setIsVisible] = useState(visible);
  useEffect(() => {
    setIsVisible(visible);
  }, [visible]);

  const [isExpanded, setIsExpanded] = useState(expand);
  useEffect(() => {
    setIsExpanded(expand);
  }, [expand]);

  const [isActive, setIsActive] = useState(action);
  useEffect(() => {
    setIsActive(action);
  }, [action]);

  const handleCardClick = (e) => {
    if (
      e.target.classList.contains("MuiCardActions-root") ||
      e.target.classList.contains("MuiTypography-root")
    ) {
      setIsExpanded(!isExpanded);
      if (expandHandler) {
        expandHandler(!isExpanded);
      }
    }
  };

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
    if (expandHandler) {
      expandHandler(!isExpanded);
    }
  };

  const handleCloseClick = () => {
    setIsVisible(!isVisible);
    if (visibleHandler) {
      visibleHandler(!isVisible);
    }
  };

  useEffect(() => {
    if (getContainerRef) {
      getContainerRef(containerRef);
    }
  }, [containerRef, getContainerRef]);

  return (
    <Card sx={card_} style={isVisible ? {} : { display: "none" }}>
      <CardActions
        disableSpacing
        sx={[cardAction_, isActive && cardActionFlash_]}
        onClick={handleCardClick}
      >
        <IconButton sx={closeBtn_} onClick={handleCloseClick} size="large">
          <CloseIcon />
        </IconButton>
        <Typography variant="button" data-testid="accordion-title">
          {title || ""}
        </Typography>
        {subtitle && (
          <Typography
            variant="overline"
            component="div"
            sx={subtitle_}
            data-testid="accordion-subtitle"
          >
            {subtitle}
          </Typography>
        )}
        <IconButton
          sx={[expand_, isExpanded && expandOpen_]}
          onClick={handleExpandClick}
          aria-expanded={isExpanded}
          aria-label="show more"
          size="large"
        >
          <ExpandMoreIcon />
        </IconButton>
      </CardActions>
      <Collapse in={isExpanded} timeout="auto">
        <CardContent sx={cardContent_} ref={containerRef}>
          {React.cloneElement(children, {})}
        </CardContent>
      </Collapse>
    </Card>
  );
}
